import React, { useState } from "react";
import { useDispatch } from "react-redux";
import CheckBox from "components/form/CheckBox";
import Button from "components/form/Button";
import usePermission from "hooks/usePermission";
import ActionFormPopup from "../ActionFormPopup";
import RequestVerificationModal from "../RequestVerificationModal";
import VerifyOtpModel from "../VerifyOtpModel";
import { getDataFromLocalStorage, objectToFormData } from "utils/helpers";
import { showSuccess, throwError } from "store/slices";

const BulkAction = ({
  showSelectAll = true,
  showApprove = true,
  showReject = true,
  isCheckAll,
  handleSelectAllChange,
  isLoading,
  selectedIds,
  setSelectedIds,
  handleSendOtp,             // passed from parent api call
  handleVerifyOtpRequest,    // passed from parent api call
  typeTitle = "Request",
  onSuccess,
  api_type
}) => {
  const dispatch = useDispatch();
  const userType = getDataFromLocalStorage("user_type");
  const { createAccess } = usePermission("Requests");

  const [openActionForm, setOpenActionForm] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [otpData, setOtpData] = useState({});
  const [btnLoading, setBtnLoading] = useState(false);
  const [otpVerification, setOtpVerification] = useState(false);
  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [isOpenConfirmationDialog, setIsOpenConfirmationDialog] = useState(false);

  const initialValues = { comment: "", document: "" };

  const sendOtpRequest = async (payload) => {
    setBtnLoading(true);
    const payloadData = objectToFormData({ request_ids: selectedIds, ...(api_type && { type: api_type }), action: payload?.action, is_bulk: 1 });
    const response = await dispatch(handleSendOtp(payloadData));
    setBtnLoading(false);

    if (response.status === 200) {
      setShowOtpDialog(true);
      setOpenActionForm(false);
      dispatch(showSuccess(response?.message));
    } else dispatch(throwError(response));
  };


  const handleReject = async (values, setErrors) => {
    setIsRejecting(true);
    const { comment, document } = values;

    if (!comment.trim() && !document) {
      const errMsg = "Either a comment or a document is required";
      setErrors({ comment: errMsg, document: errMsg });
      return;
    }
    const payload = { comment, document, action: "2" };
    setOtpData(payload);
    await sendOtpRequest(payload);
  };

  const handleApproveRequest = async (values) => {
    const { comment, document } = values;
    const payload = { comment, document, action: "1" };
    setOtpData(payload);
    if (userType === "1") {
      await sendOtpRequest(payload);
    } else {
      setIsOpenConfirmationDialog(true);
      setOpenActionForm(false);
    }
  };

  const handleVerifyOtp = async (otp) => {
    setOtpVerification(true);

    const payload = objectToFormData({
      request_ids: selectedIds,
      ...(otp && { otp }),
      document: otpData?.document,
      comment: otpData?.comment,
      request_status: otpData?.action,
      request_data: 1,
      is_bulk: 1
    });

    const response = await dispatch(handleVerifyOtpRequest(payload));

    if (response.status === 200) {
      setOtpVerification(false);
      setShowOtpDialog(false);
      setIsOpenConfirmationDialog(false);
      dispatch(showSuccess(response.message));
      setSelectedIds([]);
      onSuccess()
    } else dispatch(throwError(response));

    setOtpVerification(false);
  };

  return (
    <>
      {/* Action Form Popup */}
      {openActionForm && (
        <ActionFormPopup
          showApproveDialog={openActionForm}
          onHide={() => {
            setOpenActionForm(false)
            setOtpData({})
          }}
          setIsRejecting={setIsRejecting}
          isRejecting={isRejecting}
          handleApproveRequest={handleApproveRequest}
          handleReject={handleReject}
          initialValues={initialValues}
          btnLoading={btnLoading}
          title={`${typeTitle} Request`}
        />
      )}

      {/* Confirmation Modal */}
      {isOpenConfirmationDialog && (
        <RequestVerificationModal
          handleConfirmation={() => handleVerifyOtp()}
          onHide={() => {
            setIsOpenConfirmationDialog(false)
            setOtpData({})
          }
          }
          title="You want to share the approve request to super admin"
          isLoading={otpVerification}
        />
      )}

      {/* OTP Modal */}
      {showOtpDialog && (
        <VerifyOtpModel
          title={`${typeTitle} Request`}
          showApproveDialog={showOtpDialog}
          onHide={() => {
            setShowOtpDialog(false);
            setOtpData({})
          }}
          onVerify={handleVerifyOtp}
          sendOtpRequest={sendOtpRequest}
          otpVerification={otpVerification}
        />
      )}

      {/* Buttons */}
      <div className="d-flex gap-2 h-35 cmt-15 cmb-15">
        {showSelectAll && (
          <div className="d-flex align-items-center gap-2 p-2 text-14-400-18 br-6 bg-f4fc">
            <CheckBox
              type="ACTIVE"
              isChecked={isCheckAll}
              onClick={handleSelectAllChange}
            />
            <span>{`Select All ${typeTitle}`}</span>
          </div>
        )}

        {createAccess === "1" && (
          <>
            {showApprove && (
              <div className="d-flex align-items-center gap-2 p-2 text-14-400-18 br-6 bg-f4fc">
                <Button
                  text={["0", "1"].includes(userType) ? "Approve" : "Send Request"}
                  className="h-35 border-0"
                  disabled={isLoading}
                  onClick={() => setOpenActionForm(true)}
                />
              </div>
            )}
            {showReject && ["0", "1"].includes(userType) && (
              <div className="d-flex align-items-center gap-2 p-2 text-14-400-18 br-6 bg-f4fc">
                <Button
                  text="Reject"
                  className="h-35 border-0"
                  onClick={() => setOpenActionForm(true)}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default BulkAction;
